import { cycleTagIn, tagStateIn, type TagSelection, type TagState } from '../tagSelection';

/**
 * One chip per tag, cycling required → excluded → neither.
 *
 * Shared by the tournament list's filter bar and the leaderboard builder's tournament picker, so
 * the same gesture means the same thing in both. Two chip rows — one to require, one to exclude —
 * would list the vocabulary twice and double a control that is already competing for space.
 *
 * Excluded is deliberately *not* the signal orange: in room-web orange means "you must act", and
 * a filter setting is not a job to do. Struck through with a leading minus carries "not this"
 * without spending the accent.
 */
const CHIP: Record<TagState, string> = {
  off: 'rounded-full border border-ink-800/15 px-2.5 py-1 text-xs text-ink-800/70 hover:border-felt-500',
  include: 'rounded-full bg-felt-700 px-2.5 py-1 text-xs font-medium text-white',
  exclude:
    'rounded-full border border-ink-800/40 bg-ink-950/5 px-2.5 py-1 text-xs text-ink-800/60 line-through decoration-ink-800/50',
};

/** Spoken state, because strike-through and a colour say nothing to a screen reader. */
const LABEL: Record<TagState, (tag: string) => string> = {
  off: (tag) => `${tag}: not filtered. Activate to require it.`,
  include: (tag) => `${tag}: required. Activate to exclude it.`,
  exclude: (tag) => `${tag}: excluded. Activate to stop filtering on it.`,
};

export function TagFilterPicker({
  tags,
  value,
  onChange,
  className = '',
}: {
  /** The vocabulary to offer. Order is the caller's — alphabetical here, by use elsewhere. */
  tags: string[];
  value: TagSelection;
  onChange: (next: TagSelection) => void;
  className?: string;
}) {
  if (tags.length === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {tags.map((tag) => {
        const state = tagStateIn(value, tag);
        return (
          <button
            key={tag}
            type="button"
            onClick={() => onChange(cycleTagIn(value, tag))}
            aria-label={LABEL[state](tag)}
            className={CHIP[state]}
          >
            {state === 'exclude' ? '− ' : ''}
            {tag}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The line under the chips. Says which quantifier is in play rather than leaving it to be
 * inferred from the colours — the two rules differ, and that is the whole point of the control.
 */
export function tagPickerHint(value: TagSelection): string {
  if (value.exclude.length > 0) {
    return 'Struck-through tags are excluded — any one of them hides a tournament.';
  }
  if (value.include.length > 1) return 'Showing tournaments that carry all of the selected tags.';
  return 'Click once to require a tag, twice to exclude it.';
}
